import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

import { NumberInput } from 'ui-components/components/number-input';

import { useForecastingData } from '../../../hooks/use-forecasting-data';
import style from '../forecasting-grid.module.less';
import messages from '../messages';

/**
 * @typedef {object} OriginalRowData
 * @property {string} id - Row ID
 * @property {number} suggestedReorder - Suggested reorder quantity
 */

/**
 * @typedef {object} QuantityRowData
 * @property {OriginalRowData} original - Original row data with quantity property
 */

/**
 * Editable quantity column component for the forecasting grid
 * @param {object} props - Component props
 * @param {QuantityRowData} props.row - Row data
 * @param {(rowId: string, value: number) => void} props.updateData - Function to update data
 * @param {boolean} [props.showUpsell] - Whether to show the upsell mode (disable input)
 * @param {boolean} [props.isLoading] - Whether the data is loading
 * @returns {React.ReactElement} The rendered Quantity column component
 */
export const QuantityColumn = ({ row: { original }, updateData, showUpsell = false, isLoading = false }) => {
  const intl = useIntl();
  const { quantityValues } = useForecastingData();
  const currentQuantity = quantityValues[original.id] || original.suggestedReorder;

  const [value, setValue] = useState(currentQuantity);
  const [committedValue, setCommittedValue] = useState(currentQuantity);

  // Update value when quantity changes from context
  useEffect(() => {
    setValue(currentQuantity);
    setCommittedValue(currentQuantity);
  }, [currentQuantity]);

  const onChange = (newValue) => {
    const parsedValue = newValue === '' ? 0 : parseInt(newValue, 10);
    setValue(parsedValue);
  };

  // Update the external data only when the input loses focus and value has changed
  const onBlur = () => {
    if (value !== committedValue) {
      updateData(original.id, value);
      setCommittedValue(value);
    }
  };

  return (
    <NumberInput
      aria-label={intl.formatMessage(messages.quantityInputAriaLabel)}
      className={classNames(style['quantity-input'])}
      data-ignore-root-focus='true'
      disabled={showUpsell || isLoading}
      min={0}
      onBlur={onBlur}
      onChange={onChange}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.target.blur();
        }
      }}
      testId='quantity-input'
      value={value}
    />
  );
};
